import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, Youtube, ArrowRight, Check } from 'lucide-react';
console.log('FOOTER PULSIA RENDERED');

const footerLinks = {
  navigation: [
    { name: 'Accueil', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Accompagnements', path: '/accompagnements' },
    { name: 'Méthode Pulsia', path: '/methode' },
    { name: 'À propos', path: '/a-propos' },
  ],
  services: [
    { name: 'Sites web intelligents', path: '/solutions#sites' },
    { name: 'Assistants IA', path: '/solutions#ia' },
    { name: 'Automatisation', path: '/solutions#automation' },
    { name: 'Acquisition & Visibilité', path: '/solutions#acquisition' },
  ],
  legal: [
    { name: 'Mentions légales', path: '/mentions-legales' },
    { name: 'Politique de confidentialité', path: '/mentions-legales' },
    { name: 'CGV', path: '/conditions-generales-de-vente' },
    { name: 'Connexion Alxio🤖', path: 'https://pulsia.convocore.app/' },
  ],
};

export const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const navigate = useNavigate();

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      alert('Veuillez entrer une adresse email valide.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://hook.eu1.make.com/qwtd252lll1k7iqoigpxr3d8ecqdmko5', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: '',
          email: newsletterEmail,
          phone: '',
          company: '',
          need: 'newsletter',
          message: '',
          consent_accepted: true,
          consent_text: '',
          consent_url: '',
          source: 'Site Pulsia - Newsletter',
          submitted_at: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setIsSubscribed(true);
        setNewsletterEmail('');
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'inscription. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative bg-gradient-subtle border-t border-border/50">
      {/* Main Footer */}
      <div className="section-container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a href="/" onClick={handleLogoClick} className="flex items-center mb-6 cursor-pointer">
              <img 
                src="\wp-content\uploads\2024\05\LOGO-PULSIA-1d.png" 
                alt="Pulsia.ai" 
                className="h-10 w-auto"
              />
            </a>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm">
              Cabinet de stratégie digitale & d'architecture IA. Nous accompagnons les TPE/PME dans la mise en place de systèmes intelligents pour structurer et accélérer leur croissance.
            </p>
            
            {/* Newsletter */}
            <div className="mb-6">
              <p className="text-sm font-medium text-foreground mb-3">Restez informé</p>
              {isSubscribed ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/20">
                  <Check className="w-5 h-5 text-success" />
                  <span className="text-sm text-success">Merci pour votre inscription !</span>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Votre email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="bg-card border-border/50 focus:border-primary"
                    required
                  />
                  <Button 
                    type="submit" 
                    variant="default" 
                    size="icon"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Social Links */}
            <div className="flex gap-3">
              <a
                href="https://www.linkedin.com/in/jero-bizz/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-card border border-border/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href="https://www.facebook.com/AgencePulsia"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-card border border-border/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/agencepulsia"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-card border border-border/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.youtube.com/@jero_bizz"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-card border border-border/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Navigation Links */}
          <div>
            <h4 className="font-display font-semibold text-foreground mb-4">Navigation</h4>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-display font-semibold text-foreground mb-4">Solutions</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-display font-semibold text-foreground mb-4">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-primary mt-1" />
                <a
                  href="mailto:hello@pulsia.fr"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  hello@pulsia.fr
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-primary mt-1" />
                <a
                  href="tel:+262262790017"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  +262 262 79 00 17
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary mt-1" />
                <span className="text-sm text-muted-foreground">
                  La Réunion, France
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="section-container py-6 border-t border-border/30">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} PULSIA. Tous droits réservés.
          </p>
          <div className="flex items-center gap-6">
            {footerLinks.legal.map((link) => (
              link.path.startsWith('http') ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.name}
                </Link>
              )
            ))}
            <a
              href="https://statuspage.incident.io/pulsia-autopilote"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Statut du systeme
            </a>
            <a
              href="https://billing.stripe.com/p/login/14k2a50q5aZBg123cc"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Portail client
            </a>
            <a
              href="https://traiteurflow.pulsia.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Traiteurflow.ai
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
